/**
 * Run-hydration helpers — Spec 18 P2.
 *
 * Pure functions that turn a backend run snapshot
 * (``GET /api/runs/{id}`` + ``GET /api/runs/{id}/all-sections``) into
 * the shape the Variant C shell needs to rebuild its state after a
 * refresh / re-login:
 *
 *   - ``hydrateSpineSections`` — merges completed-module ids + the
 *     currently-drafting module into the spine section list.
 *   - ``synthesiseDraftEvents`` — produces ``AppendInput`` entries
 *     for each completed section so the EventLog (Spec 17) carries
 *     at least a "Drafted §X" backfill. Richer history (web / askwhy
 *     / regen) requires a backend ``/api/runs/{id}/events`` endpoint
 *     — see Spec 18 §0 deferred item P8.
 *   - ``deriveRunSummaryFromSpine`` — tallies the RunSummary the shell's
 *     RUN_COMPLETED dispatch carries from a hydrated spine section list,
 *     matching the live SSE path's derivation.
 *
 * SRP: every function is pure (no React, no network, no storage).
 * The shell owns side effects (API calls, dispatches, SSE resume).
 *
 * # DECISION: Synthesised draft events use an "anchored + spaced"
 * #   timestamping: started_at is the anchor, then each section's ts
 * #   is spaced evenly up to (now - 1s).
 * # WHY: We don't have per-section timing in the sections snapshot.
 * #      Collapsing every backfilled event onto a single ts would
 * #      lie about the run's pacing; spacing them evenly across the
 * #      observed run duration is a fairer approximation. The
 * #      "(now - 1s)" upper bound ensures backfilled events sort
 * #      strictly BEFORE any live event that arrives after
 * #      hydration. Timeline display order stays honest.
 * # DATE: 2026-04-21
 *
 * # DECISION: Synthesised events set runId when available, but
 * #   leave actor="asthra" regardless.
 * # WHY: the "Drafted §X" event is always asthra-originated; that's
 * #      definitional. Writer-originated events (prompts, ask-whys)
 * #      aren't re-synthesisable from the sections snapshot, so
 * #      they're excluded rather than faked.
 * # DATE: 2026-04-21
 */

import type { DocTypeConfig, SpineDTO, SpineItemDTO, TimelineDTO } from "../../api/client";
import type { RunStatus } from "../../types/index";
import type { AppendInput, EventPayload } from "../eventLog";
import type { SpineSection } from "../hooks/useVariantCRun";
import { formatSectionLabelFromNumber } from "../sectionLabel";
import { normalizeSpineLifecycle, type SpineSectionStatus } from "../sseAdapter";
import type { RunError, RunSummary } from "../state";
import { orderSpineSections } from "../utils/spineOrder";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shape returned by ``GET /api/runs/{id}/all-sections`` — matches the
 *  signature of ``getAllSectionContent`` in ``api/client.ts``. Mirrored
 *  here as a local alias so callers don't have to pass the full
 *  Array<{ ... }> chain. */
export interface AllSectionEntry {
  section_number: string;
  section_title: string;
  draft_content: string;
  module_id: string;
  title_fields?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Spine hydration
// ---------------------------------------------------------------------------

/**
 * Merge run status into an existing spine section list.
 *
 * - Sections in ``status.completed_module_ids`` become ``drafted``.
 * - ``status.current_module`` (if any) becomes ``drafting``.
 * - Sections not in either list keep their incoming status (typically
 *   ``queued`` from the fresh-spine pass).
 *
 * Does NOT mutate the input. Returns a new array with new section
 * objects (React reference-equality reasons — stale renders otherwise).
 */
export function hydrateSpineSections(sections: readonly SpineSection[], status: RunStatus): SpineSection[] {
  // TODO (Phase 4): Replace this derivation with a call to
  // ``fetchSpine(docId)`` once ``GET /api/sessions/{docId}/spine``
  // is deployed. That endpoint returns full SpineSection[] directly
  // from DB (wordCount, citationCount, acceptedAt, quality,
  // flaggedCount) instead of reconstructing status from the
  // ``completed_module_ids`` list, which carries no quality data.
  const completed = new Set(status.completed_module_ids ?? []);
  const current = status.current_module ?? null;
  return sections.map((s) => {
    if (completed.has(s.id)) {
      return { ...s, status: "drafted" as const };
    }
    if (current && s.id === current) {
      return { ...s, status: "drafting" as const };
    }
    return s;
  });
}

/**
 * Lifecycle ordering for a section's status. Higher = further along.
 * ``needs_approval`` / ``flagged`` outrank ``drafted`` because both
 * imply the section was drafted AND then carried a writer-facing
 * overlay (verdict popout / reviewer flag) — reconciliation must not
 * strip that overlay back to plain ``drafted``.
 */
const STATUS_RANK: Record<SpineSectionStatus, number> = {
  queued: 0,
  // Activation-skipped ranks with queued — an explicit re-run of the
  // section (which overrides the skip server-side) must promote it.
  skipped: 0,
  drafting: 1,
  drafted: 2,
  needs_approval: 3,
  flagged: 3,
};

function promoteAncestorDraftedStatuses(
  sections: readonly SpineSection[],
  serverReportedIds: ReadonlySet<string>,
): SpineSection[] {
  const indexByNumber = new Map<string, number>();
  for (const [index, section] of sections.entries()) {
    indexByNumber.set(section.number, index);
  }

  const ancestorNumbers = new Set<string>();
  for (const section of sections) {
    if (STATUS_RANK[section.status] < STATUS_RANK.drafted) {
      continue;
    }
    let dot = section.number.lastIndexOf(".");
    while (dot > 0) {
      ancestorNumbers.add(section.number.slice(0, dot));
      dot = section.number.lastIndexOf(".", dot - 1);
    }
  }

  if (ancestorNumbers.size === 0) {
    return [...sections];
  }

  const next = [...sections];
  for (const ancestor of ancestorNumbers) {
    const index = indexByNumber.get(ancestor);
    if (index === undefined) {
      continue;
    }
    const row = next[index];
    // A row the server reported is a module of this run with its own body:
    // its status is authoritative. Promoting it because a CHILD drafted
    // showed failed / undrafted parents (e.g. CSR 12.4 when only 12.4.2.1
    // drafted) as green, inflated the drafted count and 404'd on open.
    if (serverReportedIds.has(row.id)) {
      continue;
    }
    if (STATUS_RANK[row.status] >= STATUS_RANK.drafted) {
      continue;
    }
    next[index] = { ...row, status: "drafted" };
  }

  return next;
}

/**
 * Reconcile a spine section list (typically rebuilt from a persisted
 * UI snapshot via ``mapSpineDTO``) against the AUTHORITATIVE live run
 * ``status`` from ``GET /api/runs/{id}``.
 *
 * The persisted snapshot can be stale — it's saved opportunistically
 * and may lag the backend by one or more completed modules. The run
 * status (``completed_module_ids`` / ``current_module``) is the source
 * of truth for progress. Without this reconciliation, a refresh shows
 * a section the backend already finished as ``queued`` (pending) and
 * the spine's "N/M drafted" counter lags behind the run status.
 *
 * Rules (PROMOTE-ONLY for LIVE runs — never moves a section backwards):
 *   - A module in ``completed_module_ids`` is promoted to ``drafted``
 *     when its snapshot status is below the drafted tier (``queued`` /
 *     ``drafting``). A snapshot already at ``drafted`` /
 *     ``needs_approval`` / ``flagged`` is preserved so verdict /
 *     reviewer overlays survive the refresh.
 *   - ``current_module`` is promoted to ``drafting`` ONLY when it is
 *     NOT also in ``completed_module_ids`` and is still ``queued``.
 *     This guards against the backend reporting ``current_module`` as
 *     the LAST COMPLETED module between modules, which would otherwise
 *     paint a finished section as an in-progress (pulsing) dot.
 *
 * Terminal exception (completed / cancelled / failed): a terminal run
 * has nothing actively drafting, so ``current_module`` is NEVER painted
 * as ``drafting`` and any leftover ``drafting`` status (e.g. the module
 * the run was mid-draft on when the writer killed it) is DEMOTED back to
 * ``queued``. Without this, killing a run while a section is drafting
 * leaves that section pulsing forever after a refresh — the module never
 * finished, so it's re-draftable (``queued``), not in-progress.
 *
 * Live-drafting exception: on a NON-terminal run, a row already marked
 * ``drafting`` keeps that status — only ``completed_module_ids`` may
 * overrule it. This is what lets a mid-regen refresh survive: a
 * regenerating section still carries its PRIOR draft's word count, so the
 * ``hasDraftContent`` promotion below would otherwise flip the live regen
 * to ``drafted`` and drop the writer's only sign the work is running.
 *
 * Pure; returns a new array with new section objects only where a
 * status actually changed (reference-stable otherwise).
 */
export function reconcileSpineWithRunStatus(sections: readonly SpineSection[], status: RunStatus): SpineSection[] {
  const completed = new Set(status.completed_module_ids ?? []);
  const current = status.current_module ?? null;
  const isTerminal = status.status === "completed" || status.status === "cancelled" || status.status === "failed";
  // A section carrying persisted content (DB-backed word/citation counts) IS
  // drafted, even if the status endpoint's ``completed_module_ids`` hasn't
  // caught up yet. This lag bites the LAST section hardest: its audit/persist
  // can settle after the status snapshot is taken, so it's briefly absent from
  // ``completed_module_ids``. Without this guard the terminal branch below
  // would DOWNGRADE such a section from ``drafting`` to ``queued`` — the
  // "last section shows no output/status" report — even though its content is
  // durable. Never downgrade a section with evidence of a real draft.
  const hasDraftContent = (s: SpineSection): boolean => (s.wordCount ?? 0) > 0 || (s.citationCount ?? 0) > 0;
  const promoteToDrafted = (s: SpineSection): SpineSection =>
    STATUS_RANK[s.status] < STATUS_RANK.drafted ? { ...s, status: "drafted" as const } : s;
  return sections.map((s) => {
    // ``completed_module_ids`` is the run's own word that the module is
    // done — it outranks any local pulse, so a stale ``drafting`` row
    // still settles to ``drafted`` here.
    if (completed.has(s.id)) return promoteToDrafted(s);
    // A REGEN in flight. ``hasDraftContent`` below would promote this row
    // to ``drafted`` on its word count alone — and a regenerating section
    // always has one, because the backend's ``reset_for_regen`` clears
    // ``draft_full`` / ``draft_length`` but deliberately keeps
    // ``draft_word_count`` (it archives it into ``draft_history``), and the
    // spine endpoint sources ``word_count`` straight from that field. So
    // the heuristic fires on exactly the section that is mid-regen and
    // repaints the live hot-orange node green on refresh. On a live run an
    // explicit ``drafting`` is the freshest fact available — trust it.
    if (!isTerminal && s.status === "drafting") return s;
    if (hasDraftContent(s)) return promoteToDrafted(s);
    if (isTerminal) {
      // No live drafting on a terminal run — clear any stale pulse.
      return s.status === "drafting" ? { ...s, status: "queued" as const } : s;
    }
    if (current && s.id === current && s.status === "queued") {
      return { ...s, status: "drafting" as const };
    }
    return s;
  });
}
// ---------------------------------------------------------------------------
// EventLog synthesis
// ---------------------------------------------------------------------------

/**
 * Produce one ``AppendInput`` per completed section in the snapshot.
 * Each carries a ``draft`` payload with wordCount derived from the
 * draft text (best-effort split on whitespace; undefined when the
 * content is empty). Timestamps are anchored + evenly spaced across
 * the run's observed duration.
 *
 * Returns an empty array when no sections have draft content — a
 * freshly-started run with no completed modules hydrates to a blank
 * EventLog, which is correct.
 */
export function synthesiseDraftEvents(
  allSections: readonly AllSectionEntry[],
  status: RunStatus,
  now: number = Date.now(),
): AppendInput[] {
  // TODO (Phase 4): Replace synthesised events with a real timeline
  // fetched from ``GET /api/runs/{runId}/timeline`` once that
  // endpoint is deployed. Synthesised timestamps are spaced evenly
  // across the run duration (``spacedTimestamps`` below) and are
  // therefore approximate; the real endpoint will return the exact
  // wall-clock times stored in ``DraftRunDoc.modules[].completed_at``.
  const withContent = allSections.filter((s) => s.draft_content && s.draft_content.trim().length > 0);
  if (withContent.length === 0) return [];

  const timestamps = spacedTimestamps(withContent.length, status, now);
  return withContent.map((section, i) => ({
    ts: timestamps[i],
    actor: "asthra" as const,
    sectionId: section.module_id,
    // Phase 9 — capture the writer-facing label at hydration time so
    // synthesised draft entries (replayed from a resumed run) render
    // as ``§${number}`` in chat, matching live-stream entries.
    sectionLabel: formatSectionLabelFromNumber(section.section_number),
    // null so keepSessionLevelEvents preserves these entries across
    // run boundaries, matching the live-stream sseAdapter behaviour.
    runId: null,
    payload: {
      kind: "draft" as const,
      wordCount: wordCountOf(section.draft_content),
      content: section.draft_content ?? undefined,
    },
  }));
}

/**
 * Evenly spaced timestamps across the run's observed duration,
 * capped to (now - 1s) on the upper end so any live events that
 * arrive POST-hydration sort after the backfill.
 *
 * When ``started_at`` is absent or unparseable, we anchor at
 * ``now - elapsed_seconds * 1000`` (still truthful) or fall back to
 * ``now - 1 minute`` when neither is available.
 */
function spacedTimestamps(count: number, status: RunStatus, now: number): number[] {
  const upper = now - 1000;
  const lower = computeLowerAnchor(status, now);
  if (count === 1) return [lower];
  const span = Math.max(upper - lower, count);
  const step = span / (count - 1);
  return Array.from({ length: count }, (_, i) => Math.round(lower + step * i));
}

function computeLowerAnchor(status: RunStatus, now: number): number {
  if (status.started_at) {
    const parsed = Date.parse(status.started_at);
    if (Number.isFinite(parsed)) return parsed;
  }
  const elapsedMs = Math.max(0, Math.round(status.elapsed_seconds * 1000));
  if (elapsedMs > 0) return now - elapsedMs;
  return now - 60_000;
}

function wordCountOf(text: string): number | undefined {
  const clean = text.trim();
  if (!clean) return undefined;
  return clean.split(/\s+/).filter(Boolean).length;
}

// ---------------------------------------------------------------------------
// Lifecycle projection
// ---------------------------------------------------------------------------

/**
 * Tally the RunSummary the reducer's RUN_COMPLETED dispatch carries from a
 * hydrated spine section list — the SAME derivation the live SSE path uses
 * (see the RUN_COMPLETED patch in useVariantCRun's handleEvent), so every
 * post-run surface (CommandRail "Done. N/M sections drafted.", spine footer
 * "Run complete · N/M drafted", spine tab chip) reads one metric.
 *
 * The backend RunStatus scalars (``modules_total`` / ``modules_completed``)
 * are deliberately NOT used: they are run-scoped where the spine is
 * document-scoped, and they drift (checkpoint merges and continuation
 * attempts inflate ``section_responses``, whose length feeds
 * ``modules_completed``, and ``modules_total`` was snapped up to match on
 * completion). That drift is how a restored run could say "Done. 7/7
 * sections drafted." while the spine truthfully showed "2/96 drafted".
 * ``elapsedSeconds`` is the only run-status field callers should pass in.
 */
export function deriveRunSummaryFromSpine(sections: readonly SpineSection[], elapsedSeconds = 0): RunSummary {
  let drafted = 0;
  let flagged = 0;
  for (const s of sections) {
    if (s.status === "drafted") drafted += 1;
    else if (s.status === "flagged") flagged += 1;
  }
  return {
    totalSections: sections.length,
    draftedSections: drafted,
    flaggedSections: flagged,
    durationMs: Math.max(0, Math.round(elapsedSeconds * 1000)),
  };
}

/**
 * Whole-submission section denominator for in-run progress readouts (status
 * rail, post-run banner, spine tab chip, chat stats).
 *
 * `docConfig.modules` IS the server's `module_sections` — the exact same set
 * the drafts card's `sections_total` counts — so a run that drafted its
 * selected 10 of a 96-section doc type reads "10/96" here, matching the
 * Home/Drafts card, instead of the run-scoped "10/10".
 *
 * Returns `null` when the config is not loaded (or declares no modules), so
 * callers fall back to the scoped spine length rather than dividing by zero or
 * regressing to 0. Pure.
 */
export function submissionSectionTotal(docConfig: DocTypeConfig | null, submissionMode?: string | null): number | null {
  // Custom (build-on-existing-document) submissions have no template
  // denominator — the "whole submission" IS the writer's own document. Return
  // null so every readout falls back to the scoped spine length, which for a
  // custom run is exactly the number of headings (titles) in the document.
  if (submissionMode === "custom") return null;
  const n = docConfig ? Object.keys(docConfig.modules ?? {}).length : 0;
  return n > 0 ? n : null;
}

/**
 * Classify a RunStatus into one of the three resume-time lifecycle
 * categories. Pure; drives the shell's mount-time UX decision
 * (resume modal vs read-only snapshot vs normal picker).
 */
export type ResumeDisposition =
  /** Run is still live on the backend — offer Resume / Start fresh. */
  | { kind: "live" }
  /** Run is paused on the backend — frontend pause UX handles it later. */
  | { kind: "paused" }
  /** Run finished on the backend — show as read-only snapshot. */
  | { kind: "completed" }
  /** Run ended in error or cancellation — offer review + start fresh. */
  | { kind: "terminated"; reason: "failed" | "cancelled" };

export function classifyResume(status: RunStatus): ResumeDisposition {
  switch (status.status) {
    case "pending":
    case "running":
      return { kind: "live" };
    case "paused":
      return { kind: "paused" };
    case "completed":
      return { kind: "completed" };
    case "failed":
      return { kind: "terminated", reason: "failed" };
    case "cancelled":
      return { kind: "terminated", reason: "cancelled" };
    default:
      // Unknown future status string — be conservative, treat as
      // terminated-failed so the writer isn't silently resumed into
      // an ambiguous state.
      return { kind: "terminated", reason: "failed" };
  }
}

/**
 * Restore-time disposition for a run that already exists on the backend
 * (bootstrap restore, Drafts/Home Load, spine "Load doc").
 *
 * A cancelled run ALWAYS restores to the post-run surface (like a completed
 * run), even with zero drafted sections — that's where KILL lands live
 * (``handleStopRun``), so a refresh/Load must agree. A failed run restores
 * to post-run only when it drafted at least one section, so the writer sees
 * — and can load — the work finished before the failure. Restoring to idle
 * wipes ``runId`` and, on a document that was blank at mount, drops the
 * shell onto the full-screen Home scene. Only a failed run with NO drafts
 * resets to idle.
 *
 * SSOT for both ``useRunControl`` (bootstrap) and ``handleRunLoaded``
 * (Load) — they drifted once (2026-06 → 2026-07) and the Load path kept the
 * pre-fix "any terminated run → idle" rule. Keep them on this one helper.
 */
export function isTerminatedRunStatus(status: Pick<RunStatus, "status">): boolean {
  return status.status === "failed" || status.status === "cancelled";
}

export function restoresToPostRun(status: Pick<RunStatus, "status" | "modules_completed">): boolean {
  if (status.status === "completed" || status.status === "cancelled") return true;
  return isTerminatedRunStatus(status) && (status.modules_completed ?? 0) > 0;
}

export function restoresToIdle(status: Pick<RunStatus, "status" | "modules_completed">): boolean {
  return isTerminatedRunStatus(status) && !restoresToPostRun(status);
}

/** Error surfaced when a ``failed`` run is restored to post-run because it
 *  drafted at least one section — it shouldn't read as a clean completion.
 *  ``RUN_FAILED`` only records the error; the state machine is untouched. */
export const RUN_FAILED_AFTER_DRAFTING_ERROR: RunError = {
  code: "run_failed",
  message:
    "The run ended with an error after drafting — the drafted sections are preserved and can be opened or loaded.",
  recoverable: false,
};

// ---------------------------------------------------------------------------
// v2 schema DTO mappers — Phase 4
// ---------------------------------------------------------------------------

/** Drafting-failure fields for a spine row. Only a ``flagged`` row can be a
 *  failure — a stale ``module_error`` never marks a drafted row failed. */
function failureFields(item: SpineItemDTO): Pick<SpineSection, "failed" | "failureReason"> {
  const failed = item.module_error === true && normalizeSpineLifecycle(item.status) === "flagged";
  return failed ? { failed: true, failureReason: item.error ?? undefined } : {};
}

/**
 * Map a ``GET /api/sessions/{docId}/spine`` response into
 * ``SpineSection[]``, preserving transient SSE-only fields (``subline``,
 * ``sublineStartedAt``, ``retrievalProgress``) from the current in-memory
 * sections when the REST snapshot doesn't carry them.
 *
 * All persistent data (status, wordCount, citationCount, acceptedAt,
 * quality, flaggedCount, requirementCount) comes from the DB — the
 * source of truth — rather than being re-derived from heuristics.
 */
export function mapSpineDTO(dto: SpineDTO, currentSections: readonly SpineSection[]): SpineSection[] {
  // Build DTO lookup so we can merge in DTO data per-section without
  // dropping config-only modules (which the spine endpoint may omit
  // for paused / partial runs that haven't reached every scoped module).
  const dtoById = new Map(dto.spine.map((item) => [item.id, item]));
  const seen = new Set<string>();

  const mergedFromCurrent = currentSections.map((existing) => {
    const item = dtoById.get(existing.id);
    if (!item) return existing;
    seen.add(existing.id);
    return {
      id: item.id,
      number: item.number,
      title: item.title,
      status: normalizeSpineLifecycle(item.status),
      // Preserve live SSE fields the REST snapshot doesn't carry
      subline: existing.subline,
      sublineStartedAt: existing.sublineStartedAt,
      // Persistent fields — DB values are authoritative
      wordCount: item.word_count ?? existing.wordCount,
      citationCount: item.citation_count ?? existing.citationCount,
      acceptedAt: item.accepted_at ? Date.parse(item.accepted_at) : existing.acceptedAt,
      quality: item.quality ?? existing.quality,
      flaggedCount: item.flagged_count ?? existing.flaggedCount,
      contentGapCount: item.content_gap_count ?? existing.contentGapCount,
      contentFlagCounts: item.content_flag_counts ?? existing.contentFlagCounts,
      regulatorFlagCount: item.regulator_flag_count ?? existing.regulatorFlagCount,
      requirementCount: item.requirement_count ?? existing.requirementCount,
      retrievalProgress: item.retrieval_progress
        ? {
            resolved: item.retrieval_progress.resolved,
            total: item.retrieval_progress.total,
            writtenOff: item.retrieval_progress.writtenOff,
          }
        : existing.retrievalProgress,
      inputTokens: item.input_tokens ?? existing.inputTokens,
      outputTokens: item.output_tokens ?? existing.outputTokens,
      model: item.draft_model ?? existing.model,
      matchedModuleId: item.matched_module_id ?? existing.matchedModuleId,
      matchedTitle: item.matched_title ?? existing.matchedTitle,
      matchResolved: item.match_resolved ?? existing.matchResolved,
      ...failureFields(item),
    } satisfies SpineSection;
  });

  // DTO entries that weren't in currentSections (e.g. when the spine API
  // knows about a scoped module the local config didn't — "draft more"
  // modules are APPENDED to the run's scoped_module_ids server-side).
  // They are merged below and the whole list re-sorted into document
  // order, so a late-drafted §2 lands at the top, not the tail.
  const extras = dto.spine
    .filter((item) => !seen.has(item.id))
    .map(
      (item) =>
        ({
          id: item.id,
          number: item.number,
          title: item.title,
          status: normalizeSpineLifecycle(item.status),
          wordCount: item.word_count ?? undefined,
          citationCount: item.citation_count ?? undefined,
          acceptedAt: item.accepted_at ? Date.parse(item.accepted_at) : undefined,
          quality: item.quality ?? undefined,
          flaggedCount: item.flagged_count ?? undefined,
          contentGapCount: item.content_gap_count ?? undefined,
          contentFlagCounts: item.content_flag_counts ?? undefined,
          regulatorFlagCount: item.regulator_flag_count ?? undefined,
          requirementCount: item.requirement_count ?? undefined,
          retrievalProgress: item.retrieval_progress
            ? {
                resolved: item.retrieval_progress.resolved,
                total: item.retrieval_progress.total,
                writtenOff: item.retrieval_progress.writtenOff,
              }
            : undefined,
          inputTokens: item.input_tokens ?? undefined,
          outputTokens: item.output_tokens ?? undefined,
          model: item.draft_model ?? undefined,
          matchedModuleId: item.matched_module_id ?? undefined,
          matchedTitle: item.matched_title ?? undefined,
          matchResolved: item.match_resolved ?? undefined,
          instruction: item.instruction ?? undefined,
          ...failureFields(item),
        }) satisfies SpineSection,
    );

  return promoteAncestorDraftedStatuses(
    orderSpineSections([...mergedFromCurrent, ...extras]),
    new Set(dtoById.keys()),
  );
}

/**
 * Map a CUSTOM-submission spine snapshot into ``SpineSection[]``.
 *
 * Unlike {@link mapSpineDTO}, this preserves the document order the server sent
 * (custom headings often carry no number, so ``orderSpineSections`` would
 * scramble them) and does NOT merge against a template skeleton — the DTO rows
 * ARE the spine. Green dot (``drafted``) / empty (``queued``) come straight from
 * the server's has-content roll-up, and the persisted template match is carried
 * through so "Matched to template section: …" survives reload.
 *
 * The persistent per-section display fields (word/citation counts, quality, and
 * crucially the content-gap ``⚑`` and regulator ``⚖`` flag counts) are carried
 * through verbatim from the DTO — the server computes them for custom runs just
 * as it does for template runs. Dropping them here left the custom spine without
 * live missing-info / regulatory badges on every reopen, Load, or Drafts restore.
 */
export function mapCustomSpineDTO(dto: { spine: SpineItemDTO[] }): SpineSection[] {
  return dto.spine.map(
    (item) =>
      ({
        id: item.id,
        number: item.number,
        title: item.title,
        status: normalizeSpineLifecycle(item.status),
        // Persistent display fields — DB values are authoritative. Carried
        // exactly like mapSpineDTO's extras branch, minus the template merge
        // and number-sort that custom spines must not do.
        wordCount: item.word_count ?? undefined,
        citationCount: item.citation_count ?? undefined,
        acceptedAt: item.accepted_at ? Date.parse(item.accepted_at) : undefined,
        quality: item.quality ?? undefined,
        flaggedCount: item.flagged_count ?? undefined,
        contentGapCount: item.content_gap_count ?? undefined,
        contentFlagCounts: item.content_flag_counts ?? undefined,
        regulatorFlagCount: item.regulator_flag_count ?? undefined,
        requirementCount: item.requirement_count ?? undefined,
        retrievalProgress: item.retrieval_progress
          ? {
              resolved: item.retrieval_progress.resolved,
              total: item.retrieval_progress.total,
              writtenOff: item.retrieval_progress.writtenOff,
            }
          : undefined,
        inputTokens: item.input_tokens ?? undefined,
        outputTokens: item.output_tokens ?? undefined,
        model: item.draft_model ?? undefined,
        matchedModuleId: item.matched_module_id ?? undefined,
        matchedTitle: item.matched_title ?? undefined,
        matchResolved: item.match_resolved ?? undefined,
        instruction: item.instruction ?? undefined,
      }) satisfies SpineSection,
  );
}

/**
 * Map a ``GET /api/runs/{runId}/timeline`` response into
 * ``AppendInput[]``.
 *
 * Timestamps are real wall-clock values from
 * ``DraftRunDoc.gates[].modules[].completed_at`` — no synthesis.
 * Unknown payload kinds are silently dropped (same policy as
 * ``appendEvent`` in eventLog.ts) to stay forward-compatible with
 * future backend event types.
 *
 * ``runId`` is set to null for the same reason as in
 * ``synthesiseDraftEvents``: these entries represent historical work
 * and must survive ``keepSessionLevelEvents`` pruning across run
 * boundaries.
 */
export function mapTimelineDTO(dto: TimelineDTO): AppendInput[] {
  const out: AppendInput[] = [];
  for (const ev of dto.events) {
    const kind = (ev.payload as { kind?: unknown }).kind;
    if (typeof kind !== "string") continue;
    out.push({
      ts: ev.ts,
      actor: (ev.actor === "you" ? "you" : "asthra") as "asthra" | "you",
      sectionId: ev.section_id,
      sectionLabel: ev.section_label ?? undefined,
      // null so keepSessionLevelEvents preserves entries across run
      // boundaries — identical policy to synthesiseDraftEvents.
      runId: null,
      payload: ev.payload as EventPayload,
    });
  }
  return out;
}

/**
 * Stamp ``readyAfterMs`` on restored sections from the snapshot's own
 * timeline: the ``draft`` event's time (the section's saved completion)
 * minus the ``run_start`` event's time. Used when the pane did not watch the
 * run live, so it has no per-section clock of its own. Sections without a
 * draft event, or a run without a start event, are returned unchanged. Pure.
 */
export function withReadyTimes<T extends SpineSection>(
  sections: T[],
  events: readonly { ts: number; section_id: string | null; payload: Record<string, unknown> }[] | null | undefined,
): T[] {
  if (!events || events.length === 0) return sections;
  const start = events.find((e) => e.payload?.kind === "run_start")?.ts;
  if (typeof start !== "number") return sections;
  const readyAt = new Map<string, number>();
  for (const e of events) {
    if (e.payload?.kind !== "draft" || !e.section_id || typeof e.ts !== "number") continue;
    const prev = readyAt.get(e.section_id);
    if (prev === undefined || e.ts > prev) readyAt.set(e.section_id, e.ts);
  }
  if (readyAt.size === 0) return sections;
  return sections.map((s) => {
    const at = readyAt.get(s.id);
    return at !== undefined && at >= start ? { ...s, readyAfterMs: at - start } : s;
  });
}
